'use client'
import LazyImage from '@/src/components/common/ui/LazyImage'
import ApartmentIcon from '@mui/icons-material/Apartment'
import { ChevronLeft, ChevronRight } from '@mui/icons-material'
import { Box, Button, Container, IconButton, Link, Typography } from '@mui/material'
import { useLocale } from 'next-intl'
import { useState } from 'react'

export interface FeaturedDestination {
    id: string
    name: string
    city: string
    highlights: string[]
    imageUrl: string
    href: string
}

export interface TopHotel {
    id: string
    name: string
    location: string
    href: string
}

export interface HotelShowcaseProps {
    destinations: FeaturedDestination[]
    topHotels: TopHotel[]
    topHotelsTitle: string
    quickBookLabel: string
}

// Pure presentation: a Booking/Airbnb-style featured-destination carousel beside a Top Hotels
// list card. Carousel index is UI-only local state. Mirrors me-flyin's landing showcase row.
const HotelShowcase: React.FC<HotelShowcaseProps> = ({ destinations, topHotels, topHotelsTitle, quickBookLabel }) => {
    const locale = useLocale()
    const isRTL = locale === 'ar'
    const [index, setIndex] = useState(0)
    const active = destinations[index]
    const count = destinations.length
    // Fill the highlights top-to-bottom per column (mock reads down each column). ≤3 items stay
    // a single column; 4+ split into two balanced columns.
    const highlightRows =
        active.highlights.length > 3 ? Math.ceil(active.highlights.length / 2) : active.highlights.length

    const go = (direction: 'prev' | 'next') => {
        setIndex(prev => (direction === 'next' ? (prev + 1) % count : (prev - 1 + count) % count))
    }

    return (
        <Box sx={{ py: { xs: 3, md: 4 } }}>
            <Container maxWidth="xl" sx={{ px: { xs: 2, md: 3 } }}>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, alignItems: 'stretch' }}>
                    {/* Featured-destination carousel */}
                    <Box
                        sx={{
                            flex: { xs: 'auto', md: '1 1 62%' },
                            position: 'relative',
                            borderRadius: 2,
                            overflow: 'hidden',
                            height: { xs: 340, md: 480 }
                        }}
                    >
                        <Box sx={{ position: 'absolute', inset: 0 }}>
                            <LazyImage src={active.imageUrl} alt={active.city} sizes="(max-width: 900px) 100vw, 62vw" />
                        </Box>
                        {/* Legibility scrim behind the copy. No percentage stops: stylis-plugin-rtl
                            rewrites them as 100 - x without reordering, which collapses the gradient
                            to its darkest colour in Arabic. `to right` is flipped to `to left` there. */}
                        <Box
                            sx={{
                                position: 'absolute',
                                inset: 0,
                                background:
                                    'linear-gradient(to right, rgba(0,0,0,0.6), rgba(0,0,0,0.32), rgba(0,0,0,0))'
                            }}
                        />
                        <Box
                            sx={{
                                position: 'relative',
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                p: { xs: 2.5, md: 5 },
                                // Extra bottom room so the centered content clears the dots row.
                                pb: { xs: 5, md: 8 },
                                maxWidth: { xs: '100%', md: '65%' }
                            }}
                        >
                            {/* Headings pinned to the top */}
                            <Box>
                                <Typography sx={{ fontSize: { xs: 14, md: 18 }, fontWeight: 600, color: 'warning.main' }}>
                                    {active.name}
                                </Typography>
                                <Typography sx={{ fontSize: { xs: 22, md: 36 }, fontWeight: 700, color: 'neutral.white', lineHeight: 1.1, mt: 0.5 }}>
                                    {active.city}
                                </Typography>
                            </Box>
                            {/* Highlights + CTA sit just below the headings */}
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1.5, md: 2.5 }, mt: { xs: 2, md: 3 } }}>
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridAutoFlow: 'column',
                                        gridTemplateRows: {
                                            xs: `repeat(${active.highlights.length}, auto)`,
                                            sm: `repeat(${highlightRows}, auto)`
                                        },
                                        justifyContent: 'start',
                                        columnGap: 4,
                                        rowGap: { xs: 1, md: 1.25 }
                                    }}
                                >
                                    {active.highlights.map(highlight => (
                                        <Box key={highlight} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: 'neutral.white', flexShrink: 0 }} />
                                            <Typography sx={{ fontSize: { xs: 13, md: 14 }, fontWeight: 500, color: 'neutral.white' }}>{highlight}</Typography>
                                        </Box>
                                    ))}
                                </Box>
                                <Box>
                                    <Button
                                        href={active.href}
                                        target="_blank"
                                        rel="noopener"
                                        variant="customVariantPrimary"
                                        sx={{ px: { xs: 2.5, md: 3 }, py: 1, fontSize: { xs: 13, md: 14 }, fontWeight: 600, textTransform: 'none' }}
                                    >
                                        {quickBookLabel}
                                    </Button>
                                </Box>
                            </Box>
                        </Box>

                        {/* Controls: prev arrow, dots, next arrow — clustered on the start edge. */}
                        {count > 1 && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    bottom: { xs: 12, md: 28 },
                                    insetInlineStart: { xs: 12, md: 28 },
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: { xs: 1, md: 1.5 }
                                }}
                            >
                                <IconButton
                                    onClick={() => go('prev')}
                                    aria-label="Previous"
                                    size="small"
                                    sx={{ color: 'neutral.white', p: 0.5, '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' } }}
                                >
                                    {isRTL ? <ChevronRight fontSize="small" /> : <ChevronLeft fontSize="small" />}
                                </IconButton>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    {destinations.map((dest, dotIndex) => (
                                        <Box
                                            key={dest.id}
                                            component="button"
                                            type="button"
                                            aria-label={dest.city}
                                            onClick={() => setIndex(dotIndex)}
                                            sx={{
                                                border: 0,
                                                p: 0,
                                                cursor: 'pointer',
                                                height: 6,
                                                width: dotIndex === index ? 22 : 6,
                                                borderRadius: 999,
                                                bgcolor: 'neutral.white',
                                                opacity: dotIndex === index ? 1 : 0.5,
                                                transition: 'width 0.2s, opacity 0.2s'
                                            }}
                                        />
                                    ))}
                                </Box>
                                <IconButton
                                    onClick={() => go('next')}
                                    aria-label="Next"
                                    size="small"
                                    sx={{ color: 'neutral.white', p: 0.5, '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' } }}
                                >
                                    {isRTL ? <ChevronLeft fontSize="small" /> : <ChevronRight fontSize="small" />}
                                </IconButton>
                            </Box>
                        )}
                    </Box>

                    {/* Top Hotels list */}
                    <Box
                        sx={{
                            flex: { xs: 'auto', md: '1 1 38%' },
                            maxHeight: { xs: 360, md: 480 },
                            bgcolor: 'neutral.white',
                            border: 1,
                            borderColor: 'borders.default',
                            borderRadius: 2,
                            p: { xs: 2, md: 2.5 },
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                            '&:hover .top-hotels-scroll': { scrollbarColor: 'rgba(0,0,0,0.25) transparent' },
                            '&:hover .top-hotels-scroll::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,0,0,0.25)' }
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: { xs: 16, md: 18 },
                                fontWeight: 700,
                                color: 'textColors.primary',
                                pb: 1.5,
                                mb: 0.5,
                                borderBottom: 1,
                                borderColor: 'borders.default'
                            }}
                        >
                            {topHotelsTitle}
                        </Typography>
                        <Box
                            className="top-hotels-scroll"
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                flex: 1,
                                overflowY: 'auto',
                                mr: { xs: -2, md: -2.5 },
                                pr: { xs: 2, md: 2.5 },
                                // Mobile (no hover): thumb always visible. Desktop: hidden until the
                                // card is hovered (revealed by the card's :hover above).
                                scrollbarWidth: 'thin',
                                scrollbarColor: { xs: 'rgba(0,0,0,0.25) transparent', md: 'transparent transparent' },
                                '&::-webkit-scrollbar': { width: 6 },
                                '&::-webkit-scrollbar-thumb': {
                                    backgroundColor: { xs: 'rgba(0,0,0,0.25)', md: 'transparent' },
                                    borderRadius: 3
                                }
                            }}
                        >
                            {topHotels.map((hotel, hotelIndex) => (
                                <Link
                                    key={hotel.id}
                                    href={hotel.href}
                                    target="_blank"
                                    rel="noopener"
                                    underline="none"
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1.5,
                                        py: 1,
                                        borderTop: hotelIndex === 0 ? 0 : 1,
                                        borderColor: 'borders.default',
                                        '&:hover .top-hotel-name': { color: 'primary.main' }
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 40,
                                            height: 40,
                                            flexShrink: 0,
                                            borderRadius: 1.5,
                                            bgcolor: 'neutral.100',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                    >
                                        <ApartmentIcon sx={{ fontSize: 20, color: 'textColors.disabled' }} />
                                    </Box>
                                    <Box sx={{ minWidth: 0 , mb:1 }}>
                                        <Typography
                                            className="top-hotel-name"
                                            sx={{
                                                fontSize: 14,
                                                fontWeight: 600,
                                                mb: '2px',
                                                color: 'textColors.primary',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap'
                                            }}
                                        >
                                            {hotel.name}
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontSize: 12,
                                                color: 'textColors.secondary',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap'
                                            }}
                                        >
                                            {hotel.location}
                                        </Typography>
                                    </Box>
                                </Link>
                            ))}
                        </Box>
                    </Box>
                </Box>
            </Container>
        </Box>
    )
}

export { HotelShowcase }
export default HotelShowcase
